import {ApolloQueryResult} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {setWindowTitle} from 'BenchlingUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as ModalContainer from 'ui-library/ModalContainer';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import {fetchUnitsOnboardingData} from './UnitDictionaryDAO';
import {UNIT_TYPES_QUERY, UnitTypesQueryUnitTypeNode} from './UnitsConstants';
import UnitTypeCreationModal, {UNIT_TYPE_CREATION_MODAL_KEY} from './UnitTypeCreationModal';
import UnitTypeSettingsTable from './UnitTypeSettingsTable';
import {isUnitDictionaryManagementEnabledForUser} from './util';

function UnitDictionaryListingContents({
  refetch,
  unitTypes,
}: {
  refetch: () => Promise<ApolloQueryResult<UnitTypesQuery>>;
  unitTypes: Array<UnitTypesQueryUnitTypeNode>;
}): JSX.Element {
  const runOnboardingWizard = useCallback(async (): Promise<void> => {
    const {siUnitsData, pdUnitsData} = await fetchUnitsOnboardingData();
    ModalContainer.open(
      UNIT_TYPE_CREATION_MODAL_KEY,
      <UnitTypeCreationModal
        refetch={refetch}
        defaultSiUnitsData={siUnitsData}
        defaultPdUnitsData={pdUnitsData}
        onRequestClose={() => {
          ModalContainer.close(UNIT_TYPE_CREATION_MODAL_KEY);
        }}
        existingUnitTypes={unitTypes}
      />
    );
  }, [refetch, unitTypes]);

  useEffect(() => {
    if (unitTypes.length === 0 && isUnitDictionaryManagementEnabledForUser()) {
      runOnboardingWizard();
    }
  }, [unitTypes, runOnboardingWizard]);

  const [selectedArchivePurposes, selectArchivePurposes] = useState<Array<string>>([
    NOT_ARCHIVED_PURPOSE_STRING,
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const trimmedSearchQuery = searchQuery.trim().toLowerCase();

  const sortedUnitTypes = naturalSortByKey(unitTypes, 'name');
  const allUnits = sortedUnitTypes.map((unitType) => unitType.units).flat();

  const unitsMatchingQueryByName =
    trimmedSearchQuery !== ''
      ? allUnits.filter((unit) => unit.name.toLowerCase().includes(trimmedSearchQuery))
      : [];

  const unitsMatchingQueryBySymbol =
    trimmedSearchQuery !== ''
      ? allUnits.filter((unit) => unit.symbol.toLowerCase().includes(trimmedSearchQuery))
      : [];

  const filteredUnitTypes: Array<UnitTypesQueryUnitTypeNode> =
    trimmedSearchQuery !== ''
      ? sortedUnitTypes.filter(
          (unitType) =>
            unitType.name.toLowerCase().includes(trimmedSearchQuery) ||
            unitType.units.some(
              (unit) => unitsMatchingQueryByName.includes(unit) || unitsMatchingQueryBySymbol.includes(unit)
            )
        )
      : sortedUnitTypes;

  const filteredUnitTypesMatchingArchivePurposes = filteredUnitTypes.filter((unitType) =>
    unitType.archiveData.archived
      ? selectedArchivePurposes.includes(unitType.archiveData.purpose!)
      : selectedArchivePurposes.includes(NOT_ARCHIVED_PURPOSE_STRING)
  );

  const unitTypeTables = filteredUnitTypesMatchingArchivePurposes.map((unitType) => (
    <UnitTypeSettingsTable
      key={unitType.name}
      unitType={unitType}
      refetch={refetch}
      queryMatchedUnitsByName={unitsMatchingQueryByName}
      queryMatchedUnitsBySymbol={unitsMatchingQueryBySymbol}
    />
  ));

  const emptyUnitDictionaryMessage =
    unitTypes.length === 0
      ? 'Your unit dictionary is empty. Reach out to your tenant admin to add units.'
      : 'No items found';

  return (
    <div {...getTestProps({component: 'UnitDictionaryListing', element: 'root'})}>
      <div className="u-lr-split">
        <Header1>Unit Dictionary</Header1>
      </div>
      <div className="u-lr-split">
        <div className="u-vertical-center-items">
          <PlainSearchInput
            className={css(styles.search)}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              setSearchQuery('');
            }}
            placeholder="Search by unit type, unit name, or symbol"
            value={searchQuery}
          />
          <ArchivePurposeDropdown
            itemIds={unitTypes.map((ut) => ut.id)}
            selectedPurposes={selectedArchivePurposes}
            onChangePurposes={(newPurposes) => {
              selectArchivePurposes(newPurposes);
            }}
          />
        </div>
        <div className={css(styles.topButtons)}>
          <Tooltip title={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
            <Button
              icon="audit-trail"
              onClick={() => {
                exportAuditLogs([
                  ...allUnits.map((unit) => unit.id),
                  ...sortedUnitTypes.map((unitType) => unitType.id),
                ]);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          </Tooltip>
          {isUnitDictionaryManagementEnabledForUser() && (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              text="Add unit type"
              onClick={() => {
                runOnboardingWizard();
              }}
              dataTestAttrs={{component: 'UnitDictionaryListing', element: 'addUnitTypeButton'}}
            />
          )}
        </div>
      </div>
      <div className={css(styles.tables)}>
        {unitTypeTables.length > 0 ? (
          unitTypeTables
        ) : (
          <span className={css(styles.fadedGray)}>{emptyUnitDictionaryMessage}</span>
        )}
      </div>
    </div>
  );
}

export default function UnitDictionaryListing(): JSX.Element {
  useEffect(() => {
    setWindowTitle('Unit Dictionary');
  }, []);

  return (
    <div className={css(styles.unitDictionaryPage)}>
      <div className={css(styles.unitDictionaryListing)}>
        <BenchlingQuery<UnitTypesQuery>
          query={UNIT_TYPES_QUERY}
          renderLoading={() => <CenteredHorizontalThrobber />}
          fetchPolicy="cache-and-network"
          renderCachedDataWhenRefetching={true}
        >
          {({unitTypes: unitTypesData}, {refetch}) => (
            <UnitDictionaryListingContents refetch={refetch} unitTypes={unitTypesData} />
          )}
        </BenchlingQuery>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  unitDictionaryListing: {
    margin: '40px',
  },
  unitDictionaryPage: {
    overflowY: 'auto',
    height: '100%',
  },
  tables: {
    marginTop: 20,
  },
  topButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 4,
  },
  search: {
    marginRight: '10px',
    marginTop: '6px',
    marginBottom: '6px',
    width: 300,
    verticalAlign: 'middle',
  },
  fadedGray: {
    color: GRAY_SEMIDARK,
  },
});
